import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import { Box } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import CloseIcon from '~assets/icons/CloseIcon'
import { OrganizationAISettings0 } from '~types'

import ChatView from './ChatView'

const ChatEmbedScreen: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { organizationHandle } = useParams<{ organizationHandle: string }>()
  const displayMode = searchParams.get('displayMode') || 'full'
  const iframe = searchParams.get('iframe')
  const testing = searchParams.get('testing')
  const [organizationSettings, setOrganizationSettings] = useState<OrganizationAISettings0>()
  const [isChatOpen, setIsChatOpen] = useState(displayMode === 'full')

  const fetchOrganizationSettings = async (): Promise<void> => {
    try {
      const response = await apiZodios.ai_getOrganizationAISettings({
        organization_handle: organizationHandle,
      })
      setOrganizationSettings(response.organization)
    } catch (error) {
      console.error('Error fetching organization settings:', error)
    }
  }

  useEffect(() => {
    fetchOrganizationSettings()
  }, [organizationHandle])

  if (!organizationSettings) {
    return null
  }

  const toggleChat = (): void => {
    setIsChatOpen(!isChatOpen)
  }

  if (displayMode === 'full') {
    return (
      <Box sx={{ height: '100vh', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <ChatView orgSettings={organizationSettings} testing={testing !== null} />
      </Box>
    )
  }

  if (iframe) {
    const currentUrl = new URL(window.location.href)
    currentUrl.search = ''
    currentUrl.searchParams.set('displayMode', 'icon')

    return (
      <Box>
        <iframe
          aria-label='Polimorphic Chat'
          src={iframe}
          style={{ width: '100vw', height: '100vh' }}
        />
        <iframe
          src={currentUrl.toString()}
          style={{
            width: 'min(600px, 90%)',
            position: 'fixed',
            bottom: 0,
            height: 'calc(100vh - 20px)',
            right: 0,
            zIndex: 2147483001,
          }}
          allowTransparency
          frameBorder={0}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        alignItems: 'flex-end',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column-reverse',
        position: 'fixed',
        padding: '10px 20px 20px 10px',
        zIndex: 2147483420,
      }}
    >
      {isChatOpen ? (
        <Box
          onClick={toggleChat}
          sx={{
            width: '50px',
            height: '50px',
            minHeight: '50px',
            borderRadius: '50%',
            backgroundColor: organizationSettings.organization.bg_color
              ? `#${organizationSettings.organization.bg_color}`
              : 'black',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <CloseIcon
            width={24}
            height={24}
            color={
              organizationSettings.organization.font_color
                ? `#${organizationSettings.organization.font_color}`
                : 'white'
            }
          />
        </Box>
      ) : (
        <Box
          onClick={toggleChat}
          sx={{
            width: '121px',
            height: '121px',
            borderRadius: '50%',
            backgroundImage: organizationSettings.chat_icon
              ? `url(${encodeURI(organizationSettings.chat_icon)})`
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: organizationSettings.chat_icon
              ? 'transparent'
              : organizationSettings.organization.bg_color
                ? `#${organizationSettings.organization.bg_color}`
                : 'black',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            zIndex: 2147483420,
          }}
        >
          {!organizationSettings.chat_icon && (
            <QuestionAnswerIcon
              style={{
                width: '80px',
                height: '80px',
                color: organizationSettings.organization.font_color
                  ? `#${organizationSettings.organization.font_color}`
                  : 'white',
              }}
            />
          )}
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          height: isChatOpen ? '100%' : '0',
          borderRadius: '5px',
          overflow: 'hidden',
          boxShadow: isChatOpen ? '0px 3px 10px 1px rgba(200, 200, 200, 0.8)' : 'none',
          marginBottom: '20px',
        }}
      >
        <ChatView orgSettings={organizationSettings} testing={testing !== null} />
      </Box>
    </Box>
  )
}

export default ChatEmbedScreen
